<script setup lang="ts">
    import type { FrontendFieldDefinition } from '@hmmh/storyblok-forms/client';
    import type { FormInputStoryblok } from '~/.storyblok/types/FormInputStoryblok';
    import FormInput from '~/storyblok/Form/FormInput.vue';
    import { isRequiredField, useFormFieldId } from './form-field-utils';

    type RenderedInputType = FormInputStoryblok['inputType'] | 'tel' | 'date';
    type RenderedInputBlok = Omit<FormInputStoryblok, 'inputType'> & {
        inputType: RenderedInputType;
    };

    const props = defineProps<{ field: FrontendFieldDefinition }>();
    const inputId = useFormFieldId();

    const inputType = computed<RenderedInputType>(() => {
        if (props.field.type === 'textarea') return 'textbox';
        if (props.field.type === 'hidden') return 'hidden';

        switch (props.field.inputType) {
            case 'email':
                return 'email';
            case 'number':
                return 'number';
            case 'tel':
                return 'tel';
            case 'date':
                return 'date';
            default:
                return 'text';
        }
    });

    const blok = computed<RenderedInputBlok>(() => ({
        _uid: inputId,
        component: 'FormInput',
        inputKey: props.field.name,
        inputType: inputType.value,
        label: props.field.label,
        placeholderText: props.field.placeholder,
        required: isRequiredField(props.field),
        Value: props.field.value
    }));
</script>

<template>
    <FormInput :blok="blok" />
</template>
